/**
 * Shared confetti engine (vendored canvas-confetti v1.9.4 - see
 * `lib/canvas-confetti.vendor.js`). WHY vendored: deploy uses
 * `pnpm install --frozen-lockfile`, so a new npm dependency cannot be added without
 * updating the lockfile. SSR-SAFE: the vendor file is loaded ONLY on the `fireConfetti`
 * call with a dynamic `import()` (in the browser), not when this module loads -
 * so Next SSR (web_site) never touches `window` references.
 *
 * The variants mirror the canvas-confetti recipe gallery and match the admin
 * Customize → Start options. Users: web_account "Your site is ready" (funnel)
 * and the web_admin "Test" button. Respects `prefers-reduced-motion` (then it does not play).
 */

export type ConfettiVariant =
  | "realistic"
  | "basic"
  | "fireworks"
  | "stars"
  | "snow"
  | "schoolPride";

type ConfettiFn = (opts: Record<string, unknown>) => void;

let enginePromise: Promise<ConfettiFn | null> | null = null;

/** Load the vendored engine lazily (in the browser). Returns `null` when it fails. */
async function loadEngine(): Promise<ConfettiFn | null> {
  if (typeof window === "undefined") return null;
  const existing = (window as unknown as { confetti?: ConfettiFn }).confetti;
  if (existing) return existing;
  if (!enginePromise) {
    enginePromise = import("./lib/canvas-confetti.vendor.js")
      .then(() => (window as unknown as { confetti?: ConfettiFn }).confetti ?? null)
      .catch(() => null);
  }
  return enginePromise;
}

/** Whether the user has asked for reduced motion - then confetti does NOT play. */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Play confetti once with the chosen variant. Purely decorative - silently
 * gives up when the engine does not load or motion is turned down.
 */
export async function fireConfetti(variant: ConfettiVariant = "realistic"): Promise<void> {
  if (prefersReducedMotion()) return;
  const confetti = await loadEngine();
  if (!confetti) return;
  runVariant(confetti, variant);
  bumpCanvasZIndex();
}

function runVariant(confetti: ConfettiFn, variant: ConfettiVariant): void {
  switch (variant) {
    case "basic":
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 }, zIndex: 99999 });
      return;

    case "fireworks": {
      const duration = 3000;
      const end = Date.now() + duration;
      const base = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 99999 };
      const interval = setInterval(() => {
        const left = end - Date.now();
        if (left <= 0) {
          clearInterval(interval);
          return;
        }
        const particleCount = 50 * (left / duration);
        confetti({ ...base, particleCount, origin: { x: Math.random() * 0.2, y: Math.random() - 0.2 } });
        confetti({ ...base, particleCount, origin: { x: 1 - Math.random() * 0.2, y: Math.random() - 0.2 } });
      }, 250);
      return;
    }

    case "stars": {
      const base = {
        spread: 360,
        ticks: 50,
        gravity: 0,
        decay: 0.94,
        startVelocity: 30,
        colors: ["FFE400", "FFBD00", "E89400", "FFCA6C", "FDFFB8"],
        zIndex: 99999,
      };
      const shoot = () => {
        confetti({ ...base, particleCount: 40, scalar: 1.2, shapes: ["star"] });
        confetti({ ...base, particleCount: 10, scalar: 0.75, shapes: ["circle"] });
      };
      shoot();
      setTimeout(shoot, 100);
      setTimeout(shoot, 200);
      return;
    }

    case "snow": {
      const duration = 5000;
      const end = Date.now() + duration;
      const frame = () => {
        confetti({
          particleCount: 1,
          startVelocity: 0,
          ticks: 200,
          origin: { x: Math.random(), y: Math.random() * 0.5 - 0.2 },
          colors: ["#ffffff"],
          shapes: ["circle"],
          gravity: 0.4,
          scalar: 0.8,
          drift: 0.4,
          zIndex: 99999,
        });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();
      return;
    }

    case "schoolPride": {
      const end = Date.now() + 3000;
      const colors = ["#bb0000", "#ffffff"];
      const frame = () => {
        confetti({ particleCount: 2, angle: 60, spread: 55, origin: { x: 0 }, colors, zIndex: 99999 });
        confetti({ particleCount: 2, angle: 120, spread: 55, origin: { x: 1 }, colors, zIndex: 99999 });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();
      return;
    }

    case "realistic":
    default: {
      const count = 200;
      const defaults = { origin: { y: 0.7 }, zIndex: 99999 };
      const fire = (ratio: number, opts: Record<string, unknown>) =>
        confetti({ ...defaults, ...opts, particleCount: Math.floor(count * ratio) });
      fire(0.25, { spread: 26, startVelocity: 55 });
      fire(0.2, { spread: 60 });
      fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
      fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
      fire(0.1, { spread: 120, startVelocity: 45 });
    }
  }
}

/**
 * canvas-confetti creates its `<canvas>` with fixed positioning; raise the z-index really
 * high to be safe + disable pointer events (otherwise it swallows clicks).
 */
function bumpCanvasZIndex(): void {
  setTimeout(() => {
    document.querySelectorAll("canvas").forEach((c) => {
      const cs = window.getComputedStyle(c);
      if (cs.position === "fixed" || cs.position === "absolute") {
        c.style.zIndex = "99999";
        c.style.pointerEvents = "none";
      }
    });
  }, 100);
}
